import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PasswordInput, AuthSubmitButton, AuthPageLayout, AuthCard } from './components';
import { validatePassword, validatePasswordConfirm } from '../../utils/validation';
import { LoadingSpinner } from '../common';
import '../../styles/components.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
      return;
    }

    // Verify token on component mount
    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
        toast.error('Invalid or expired reset link');
      }
    };

    verifyToken();
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    const confirmError = validatePasswordConfirm(formData.newPassword, formData.confirmPassword);
    if (confirmError) {
      toast.error(confirmError);
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post('/api/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });
      
      toast.success(response.data.message);
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.message || 'Error resetting password. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state - verifying token
  if (tokenValid === null) {
    return (
      <AuthPageLayout showFooter={false}>
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Verifying reset link...</p>
        </div>
      </AuthPageLayout>
    );
  }

  // Invalid token state
  if (tokenValid === false) {
    return (
      <AuthPageLayout showFooter={false}>
        <div className="text-center space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              Invalid Reset Link
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              This password reset link is invalid or has expired.
            </p>
          </div>
          
          <div className="space-y-4">
            <Link 
              to="/forgot-password" 
              className="block w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg text-center"
            >
              Request New Reset Link
            </Link>
            
            <Link 
              to="/login" 
              className="block text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  // Valid token - show reset form
  return (
    <AuthPageLayout showFooter={false}>
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            Reset Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter a new password for <strong>{userEmail}</strong>
          </p>
        </div>

        <AuthCard className="shadow-none p-0">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <PasswordInput
              id="newPassword"
              name="newPassword"
              label="New Password"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password"
              autoComplete="new-password"
            />

            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm New Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />

            <AuthSubmitButton loading={loading} loadingText="Resetting...">
              Reset Password
            </AuthSubmitButton>
          </form>
        </AuthCard>

        <div className="text-center">
          <Link 
            to="/login" 
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default ResetPassword;
